/**
 * `<ZoomControl/>` — the bottom-right `−  100%  +` zoom widget (B2.1).
 *
 * Issue #759 (Phase 2). Pure presentational; the canvas owns the zoom
 * state and passes handlers in.
 *
 * Editorial revamp (#1043, Wave 1): theme-aware. Editorial = the wireframe
 * `ZoomBar` pill (cream surface, hairline `rule` border, radius-100, mono
 * 10.5px `slate` readout). Legacy = the shipped `rounded-md` mint widget,
 * byte-identical when the team's `task_editorial_ui` flag is off.
 */

import { useMapTheme } from "../theme/MapThemeContext";

export function ZoomControl({
  zoom,
  onZoomIn,
  onZoomOut,
}: {
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
}) {
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  return (
    <div
      data-testid="zoom-control"
      className={`pointer-events-auto inline-flex items-center gap-3 border px-3 py-1.5 text-xs shadow-sm ${
        editorial ? "rounded-full" : "rounded-md"
      }`}
      style={{
        backgroundColor: editorial ? c.cardBg : "#FFFFFF",
        borderColor: editorial ? c.rule : c.softBorder,
        color: editorial ? c.slate : c.slate,
        fontFamily: editorial ? theme.fonts.mono : undefined,
        fontSize: editorial ? "10.5px" : undefined,
        letterSpacing: editorial ? "0.04em" : undefined,
      }}
    >
      <button
        type="button"
        aria-label="Zoom out"
        onClick={onZoomOut}
        className="leading-none transition-opacity hover:opacity-70"
        style={{ color: editorial ? c.slate : c.muted }}
      >
        −
      </button>
      <span className="tabular-nums" data-testid="zoom-level">
        {Math.round(zoom * 100)}%
      </span>
      <button
        type="button"
        aria-label="Zoom in"
        onClick={onZoomIn}
        className="leading-none transition-opacity hover:opacity-70"
        style={{ color: editorial ? c.slate : c.muted }}
      >
        +
      </button>
    </div>
  );
}
